import { Switch, Route } from "wouter";
import { lazy, Suspense } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/error-boundary";
import NotFound from "@/pages/not-found";
import Home from "@/pages/home";
import Landing from "@/pages/landing";
import AuthPage from "@/pages/auth";
import AuthCallbackPage from "@/pages/auth-callback";
import ResetPasswordPage from "@/pages/reset-password";
import OAuthConsentPage from "@/pages/oauth-consent";
import SignupSuccessPage from "@/pages/signup-success";
import SignupCancelPage from "@/pages/signup-cancel";
import CheckoutSuccessPage from "@/pages/checkout-success";
import CheckoutCancelPage from "@/pages/checkout-cancel";
import { useAuth } from "@/hooks/useAuth";
import { ResumeWorkoutBar } from "@/components/resume-workout-bar";
import { OfflineProvider } from "@/contexts/OfflineContext";
import { OfflineIndicator } from "@/components/offline-indicator";
import { BroadcastPopup } from "@/components/broadcast-popup";
import { useRestTimerCheck } from "@/hooks/useRestTimerCheck";

const WorkoutProgram = lazy(() => import("@/pages/workout"));
const CreateProgram = lazy(() => import("@/pages/create-program"));
const AdminDashboard = lazy(() => import("@/pages/admin-dashboard"));
const CoachDashboard = lazy(() => import("@/pages/coach-dashboard"));
const Progress = lazy(() => import("@/pages/progress"));
const CalendarPage = lazy(() => import("@/pages/calendar"));
const ImportData = lazy(() => import("@/pages/import-data"));
const Tutorial = lazy(() => import("@/pages/tutorial"));
const MusicSettings = lazy(() => import("@/pages/music-settings"));
const ArchivedPage = lazy(() => import("@/pages/archived"));
const TrainingBlockPage = lazy(() => import("@/pages/training-block"));
const TrainingBlocksListPage = lazy(() => import("@/pages/training-blocks-list"));
const Programs = lazy(() => import("@/pages/programs"));
const FavoritesPage = lazy(() => import("@/pages/favorites"));
const MyProgramsPage = lazy(() => import("@/pages/my-programs"));
const MyCoach = lazy(() => import("@/pages/my-coach"));
const Preferences = lazy(() => import("@/pages/preferences"));
const WorkoutPreferences = lazy(() => import("@/pages/workout-preferences"));
const QuickLogPage = lazy(() => import("@/pages/quick-log"));
const PrivacyPolicy = lazy(() => import("@/pages/privacy"));
const TermsOfService = lazy(() => import("@/pages/terms"));
const DebugWorkout = lazy(() => import("@/pages/debug-workout"));

function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center">
      <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
    </div>
  );
}

function Router() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <div className="font-outfit text-3xl font-bold text-primary animate-pulse">myFitBeats</div>
        <div className="mt-6 w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        {!isAuthenticated ? (
          <>
            <Route path="/" component={Landing} />
            <Route path="/auth" component={AuthPage} />
          </>
        ) : (
          <>
            <Route path="/" component={Home} />
            <Route path="/admin" component={AdminDashboard} />
            <Route path="/coach" component={CoachDashboard} />
            <Route path="/program/:id" component={WorkoutProgram} />
            <Route path="/calendar" component={CalendarPage} />
            <Route path="/create" component={CreateProgram} />
            <Route path="/progress" component={Progress} />
            <Route path="/import" component={ImportData} />
            <Route path="/tutorial" component={Tutorial} />
            <Route path="/music" component={MusicSettings} />
            <Route path="/archived" component={ArchivedPage} />
            <Route path="/training-blocks" component={TrainingBlocksListPage} />
            <Route path="/training-block/:id" component={TrainingBlockPage} />
            <Route path="/programs" component={Programs} />
            <Route path="/favorites" component={FavoritesPage} />
            <Route path="/my-programs" component={MyProgramsPage} />
            <Route path="/my-coach" component={MyCoach} />
            <Route path="/preferences" component={Preferences} />
            <Route path="/workout-preferences" component={WorkoutPreferences} />
            <Route path="/quick-log" component={QuickLogPage} />
            <Route path="/debug-workout" component={DebugWorkout} />
            <Route path="/debug-workout/:programId" component={DebugWorkout} />
          </>
        )}
        <Route path="/auth" component={AuthPage} />
        <Route path="/auth/callback" component={AuthCallbackPage} />
        <Route path="/auth/reset-password" component={ResetPasswordPage} />
        <Route path="/oauth/consent" component={OAuthConsentPage} />
        <Route path="/signup/success" component={SignupSuccessPage} />
        <Route path="/signup/cancel" component={SignupCancelPage} />
        <Route path="/checkout/success" component={CheckoutSuccessPage} />
        <Route path="/checkout/cancel" component={CheckoutCancelPage} />
        <Route path="/settings" component={Preferences} />
        <Route path="/privacy" component={PrivacyPolicy} />
        <Route path="/terms" component={TermsOfService} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function AppContent() {
  useRestTimerCheck();
  
  return (
    <>
      <OfflineIndicator />
      <Toaster />
      <BroadcastPopup />
      <Router />
      <ResumeWorkoutBar />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          storageKey="myFitBeats_theme"
          enableSystem={true}
          disableTransitionOnChange
        >
          <OfflineProvider>
            <TooltipProvider>
              <AppContent />
            </TooltipProvider>
          </OfflineProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
